import { useCallback, useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";

import { startSaveBusinessCompetitors } from "../../../../actions/adsCampaigns";
import { useTranslationApp } from "../../../../lib/i18next";
import { normalizeArrayField } from "../../../../pages/Settings/BrandSetup/utils/formDataTransformers";
import { getValidationRules } from "../../../../pages/Settings/BrandSetup/utils/validationRules";

const getDefaultCompetitorsFormValues = (businessCompetitors = []) => ({
  businessCompetitors: businessCompetitors.map((name) => name.trim()),
  newCompetitor: "",
});

export const useModalCompetitorsEditor = ({
  businessCompetitors,
  dispatch,
  modalOpen,
  onSaved,
  onCloseModal,
}) => {
  const { t } = useTranslationApp();
  const validationRules = getValidationRules(t);

  const {
    control,
    watch,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: getDefaultCompetitorsFormValues(businessCompetitors),
    mode: "onChange",
  });

  const {
    fields: businessCompetitorsFields,
    append: appendBusinessCompetitors,
    remove: removeBusinessCompetitors,
  } = useFieldArray({
    control,
    name: "businessCompetitors",
  });

  useEffect(() => {
    if (!modalOpen) return;
    reset(getDefaultCompetitorsFormValues(businessCompetitors));
  }, [businessCompetitors, modalOpen, reset]);

  const onCancelEdit = useCallback(() => {
    reset(getDefaultCompetitorsFormValues(businessCompetitors));
    onCloseModal?.();
  }, [businessCompetitors, onCloseModal, reset]);

  const onSaveCompetitors = useCallback(async () => {
    const isValid = await trigger("businessCompetitors");
    if (!isValid) {
      return { ok: false };
    }

    const formValues = getValues();
    const savedCompetitors = normalizeArrayField(
      formValues.businessCompetitors
    );

    try {
      const response = await dispatch(
        startSaveBusinessCompetitors(savedCompetitors)
      );

      if (!response?.ok) {
        return { ok: false };
      }

      await onSaved?.({ savedCompetitors });
      return { ok: true };
    } catch {
      return { ok: false };
    }
  }, [dispatch, getValues, onSaved, trigger]);

  return {
    control,
    errors,
    watch,
    setValue,
    businessCompetitorsFields,
    appendBusinessCompetitors,
    removeBusinessCompetitors,
    validationRules,
    onSaveCompetitors,
    onCancelEdit,
  };
};

export default useModalCompetitorsEditor;
